import { isEmpty, isNil } from 'lodash';
import React, { useContext, useState } from 'react';
import { RCDropdown } from '@shared/components/RCDropdown';
import { RCSubMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { RelatedCardLookupOptions } from '@shared/scenes/workflows/edit/components/optionsMenu/relatedCardLookup/RelatedCardLookupOptions';
import { ColorTheme, ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { InputComponent } from '../../InputComponent';
import {
  CombinedTagsComponent,
  ReferencedInComponent,
  RelatedCardComponent,
  RelatedCardLookupComponent,
  ScheduledAutomationComponent,
  TimeTrackerComponent,
} from '../../config';
import { WorkspaceDetail } from '../../hooks/useWorkspaceDetails';
import {
  hasDefault,
  hasSelectableDefault,
  isAuditComponent,
  isCombinedTagsComponent,
  isDateComponent,
  isDateRangeComponent,
  isInputButtonComponent,
  isInputComponent,
  isReferencedInComponent,
} from '../../utils';
import { DateAutoExtendDefaultOptions } from './optionsMenu/DateAutoExtendDefaultOptions';
import { DateRangeOptions } from './optionsMenu/DateRangeOptions';
import { InputButtonOptions } from './optionsMenu/InputButtonOptions';
import { StaticTextOptions } from './optionsMenu/StaticTextOptions';
import { SubFormOptionsChildren } from './optionsMenu/SubFormOptionsChildren';
import { TimeTrackerExtraSections } from './optionsMenu/TimeTrackerExtraSections';
import { CombinedTagsOptions } from './optionsMenu/combinedTagsOptions/CombinedTagsOptions';
import { ComponentDefaultItemRow } from './optionsMenu/componentDefaults/row/ComponentDefaultItemRow';
import { DefaultSelection } from './optionsMenu/componentDefaults/selection/DefaultSelection';
import { MBWorkflowDefaultComponentOptions } from './optionsMenu/defaultOptions';
import { MBWorkflowNumberOptions } from './optionsMenu/numberOptions';
import { ReferencedInOptions } from './optionsMenu/referencedInOptions/ReferencedInOptions';
import { RelatedCardLinkingOptions } from './optionsMenu/relatedCardOptions/RelatedCardLinkingOptions';
import { ScheduledAutomationOptions } from './optionsMenu/scheduledAutomationOptions/ScheduledAutomationOptions';
import { MBWorkflowTagOptions } from './optionsMenu/tagOptions';

interface Props {
  workflowTemplateId: string;
  component: MBAPI.WorkflowTemplate_components;
  components: MBAPI.WorkflowTemplate_components[];
  setShowingSheet(val: boolean): void;
  showingSheet: boolean;
  disableOpenSheet: boolean;
  workspaceDetails: Map<string, WorkspaceDetail>;
}

export function MBWorkflowComponentEditor({
  workflowTemplateId,
  component,
  components,
  showingSheet,
  setShowingSheet,
  disableOpenSheet,
  workspaceDetails,
}: Props) {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isIconHovered, setIsIconHovered] = useState<boolean>(false);
  const colors = useContext(ColorThemeContext);
  const icon = getIconForComponent(component);

  return (
    <MBHover
      style={{ margin: grid(2) }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <MBTouchableOpacity
        disabled={disableOpenSheet}
        onPress={() => setShowingSheet(!showingSheet)}
      >
        <View row centerVertically>
          <RCDropdown
            trigger={['click']}
            onVisibleChange={setShowingSheet}
            visible={showingSheet}
            overlay={
              <OptionsMenu
                component={component}
                components={components}
                workflowTemplateId={workflowTemplateId}
                workspaceDetails={workspaceDetails}
                showingSheet={showingSheet}
              />
            }
          >
            {isHovered ? (
              <MBHover
                onHoverStart={() => setIsIconHovered(true)}
                onHoverEnd={() => setIsIconHovered(false)}
              >
                <MBIconV2
                  name="dots-grab-narrow"
                  color={isIconHovered ? colors.input.foregroundInactive : colors.input.label}
                  size={grid(4)}
                />
              </MBHover>
            ) : (
              <View style={{ width: grid(4), height: grid(4) }} />
            )}
          </RCDropdown>
          <View row centerVertically flex>
            <View row centerVertically style={{ width: grid(4) }}>
              {!isNil(icon) && <MBIconV2 color={colors.accent} name={icon} size={grid(4)} />}
            </View>
            <View paddingHorizontal={2} flex>
              <ComponentPreviewText component={component} colors={colors} />
            </View>
          </View>
        </View>
      </MBTouchableOpacity>
    </MBHover>
  );
}

interface OptionsMenuProps {
  component: MBAPI.WorkflowTemplate_components;
  components: MBAPI.WorkflowTemplate_components[];
  workflowTemplateId: string;
  workspaceDetails: Map<string, WorkspaceDetail>;
  showingSheet: boolean;
}

function OptionsMenu({
  component,
  components,
  workflowTemplateId,
  workspaceDetails,
  showingSheet,
}: OptionsMenuProps) {
  const colors = useContext(ColorThemeContext);
  const defaultRowGenerator = useDefaultsRowSection(workflowTemplateId, component);

  switch (component.type) {
    case 'COMBINED_TAGS':
      return (
        <CombinedTagsOptions
          component={component as CombinedTagsComponent}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'TIME_TRACKER':
      return (
        <MBWorkflowDefaultComponentOptions
          component={component as InputComponent}
          includePlaceholder={false}
          extraSections={() => {
            return <TimeTrackerExtraSections component={component as TimeTrackerComponent} />;
          }}
        />
      );
    case 'REFERENCED_IN':
      return (
        <ReferencedInOptions
          component={component as ReferencedInComponent}
          workspaceDetails={workspaceDetails}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'DATE':
      return (
        <MBWorkflowDefaultComponentOptions
          component={component as InputComponent}
          extraSections={() => (
            <>
              {isDateComponent(component) &&
                !component.isServerManaged &&
                component.allowEntityBatchCreation && (
                  <DateAutoExtendDefaultOptions
                    workflowTemplateId={workflowTemplateId}
                    component={component as MBAPI.WorkflowTemplate_components_DateComponent}
                  />
                )}
            </>
          )}
        />
      );
    case 'DATE_RANGE':
      return (
        <DateRangeOptions
          component={component as MBAPI.WorkflowTemplate_components_DateRangeComponent}
        />
      );
    case 'ADDRESS':
    case 'GEOLOCATION':
    case 'TODO':
    case 'FILE':
    case 'SIGNATURE':
    case 'AUDIT_CHECKBOX':
    case 'AUDIT_TEXT':
      return <MBWorkflowDefaultComponentOptions component={component as InputComponent} />;
    case 'ENTITY_BATCH':
      return (
        <MBWorkflowDefaultComponentOptions
          component={component as InputComponent}
          includePlaceholder={false}
        />
      );
    case 'RELATED_CARD':
      return (
        <RelatedCardLinkingOptions
          component={component as RelatedCardComponent}
          workflowTemplateId={workflowTemplateId}
          workspaceDetails={workspaceDetails}
        />
      );
    case 'RELATED_CARD_LOOKUP':
      return (
        <RelatedCardLookupOptions
          component={component as RelatedCardLookupComponent}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'PERSON':
    case 'TEXT':
    case 'EMAIL':
    case 'URL':
      return (
        <MBWorkflowDefaultComponentOptions
          component={component as InputComponent}
          extraSections={defaultRowGenerator}
        />
      );
    case 'NUMBER':
      return (
        <MBWorkflowNumberOptions
          component={component as MBAPI.WorkflowTemplate_components_NumberComponent}
        />
      );
    case 'TAG':
    case 'AUDIT_TAG':
      return (
        <MBWorkflowTagOptions
          workflowTemplateId={workflowTemplateId}
          component={component as MBAPI.WorkflowTemplate_components_TagComponent}
        />
      );
    case 'STATIC_TEXT':
      return (
        <StaticTextOptions
          component={component as MBAPI.WorkflowTemplate_components_StaticTextComponent}
        />
      );
    case 'SUBFORM':
      return (
        <MBWorkflowDefaultComponentOptions
          component={component as InputComponent}
          injectSectionsIn={'bottom'}
          extraSections={() => (
            <>
              <SubFormOptionsChildren
                key={showingSheet.toString()}
                component={component as MBAPI.WorkflowTemplate_components_SubformComponent}
              />
              <View paddingHorizontal={3} paddingVertical={1}>
                <MBTextOverlineBold color={colors.foregroundInactive}>
                  {i18n('builder.defaultRow.rowHeader')}
                </MBTextOverlineBold>
              </View>
              <RCSubMenu
                key="defaultValues"
                style={{ zIndex: 999 }}
                title={<ComponentDefaultItemRow component={component as InputComponent} />}
              >
                <DefaultSelection workflowTemplateId={workflowTemplateId} component={component} />
              </RCSubMenu>
            </>
          )}
        />
      );
    case 'INPUT_BUTTON':
      return (
        <InputButtonOptions
          component={component as MBAPI.WorkflowTemplate_components_InputButtonComponent}
          components={components}
        />
      );
    case 'SCHEDULED_AUTOMATION':
      return (
        <ScheduledAutomationOptions
          component={component as ScheduledAutomationComponent}
          components={components}
          workflowTemplateId={workflowTemplateId}
        />
      );
    default:
      return <View />;
  }
}

function useDefaultsRowSection(
  workflowTemplateId: string,
  component: MBAPI.WorkflowTemplate_components,
) {
  const colors = useContext(ColorThemeContext);
  if (!isInputComponent(component)) {
    return undefined;
  }
  return function DefaultRowSection() {
    if (!hasDefault(component)) {
      return null;
    }
    return (
      <>
        <View paddingHorizontal={3} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('builder.defaultRow.rowHeader')}
          </MBTextOverlineBold>
        </View>
        {hasSelectableDefault(component) ? (
          <RCSubMenu
            key="defaultValues"
            style={{ zIndex: 999 }}
            title={<ComponentDefaultItemRow component={component} />}
          >
            <DefaultSelection workflowTemplateId={workflowTemplateId} component={component} />
          </RCSubMenu>
        ) : (
          <ComponentDefaultItemRow component={component} />
        )}
      </>
    );
  };
}

function ComponentPreviewText({
  component,
  colors,
}: {
  component: MBAPI.WorkflowTemplate_components;
  colors: ColorTheme;
}) {
  let topLabel: string | null = null;
  if (isInputButtonComponent(component)) {
    topLabel = i18n('workflowTemplate.component.type.label.button');
  } else if (isReferencedInComponent(component)) {
    topLabel = isNil(component.label)
      ? null
      : i18n('workflowTemplate.component.type.label.referencedIn');
  } else if (isInputComponent(component)) {
    topLabel = component.label;
  }

  let mainLabel: string | null = null;
  if (
    isInputComponent(component) &&
    !isInputButtonComponent(component) &&
    !isNil(component.placeholder)
  ) {
    mainLabel = component.placeholder;
  } else if (isReferencedInComponent(component)) {
    mainLabel = !isNil(component.label)
      ? component.label
      : i18n('workflowTemplate.component.type.label.referencedIn');
  } else if (isAuditComponent(component)) {
    mainLabel = component.label;
  } else if (isInputButtonComponent(component)) {
    mainLabel = component.buttonText;
  } else if (isCombinedTagsComponent(component)) {
    mainLabel = component.label;
  } else if (isDateRangeComponent(component)) {
    mainLabel = component.label;
  } else if (!isInputComponent(component)) {
    mainLabel = component.text;
  }

  if (isEmpty(mainLabel) && !isNil(topLabel)) {
    mainLabel = topLabel;
    topLabel = null;
  }

  if (topLabel === mainLabel) {
    topLabel = null;
  }

  return (
    <>
      {!isNil(topLabel) && (
        <MBTextOverlineBold color={colors.input.label}>{topLabel}</MBTextOverlineBold>
      )}
      {!isNil(mainLabel) && (
        <MBTextBody1 color={colors.foregroundInactive}>{mainLabel}</MBTextBody1>
      )}
    </>
  );
}

function getIconForComponent(
  component: MBAPI.WorkflowTemplate_components,
): MBIconV2Name | undefined {
  if ('icon' in component) {
    return component.icon as MBIconV2Name;
  } else if (isReferencedInComponent(component)) {
    return 'arrow-move-right';
  } else if (isInputButtonComponent(component)) {
    return 'button-press';
  }
}
